import { useEffect, useMemo, useState } from 'react';
import type { PlatformSettings } from '@techsofit/api-types';
import { Card, Skeleton, cn } from '@techsofit/ui';
import {
  getGyms,
  getNotificationStats,
  getPlatformNotifications,
  getPlatformSettings,
  updatePlatformSettings,
} from '../lib/adminApi';
import { useAsync } from '../lib/useAsync';

/** YYYY-MM-DD for today shifted by `deltaDays` (local calendar, interpreted server-side as Harare). */
function isoDay(deltaDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

interface Range {
  label: string;
  from: string;
  to: string;
}

const PRESETS: Range[] = [
  { label: 'Today', from: isoDay(0), to: isoDay(0) },
  { label: 'Last 7 days', from: isoDay(-6), to: isoDay(0) },
  { label: 'Last 30 days', from: isoDay(-29), to: isoDay(0) },
  { label: 'All time', from: '', to: '' },
];

/** Sent-messages log + per-channel counts + approx SMS cost, with editable per-network rates.
 *  Rendered as a tab inside the Notifications page. */
export function SentMessagesTab() {
  // Default to the last 30 days so the list and the stat cards line up.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [gymId, setGymId] = useState('');

  const { data: gyms } = useAsync(getGyms);
  const { data: stats } = useAsync(() => getNotificationStats(30));
  const { data: recent, loading } = useAsync(
    () =>
      getPlatformNotifications({
        limit: 300,
        gymId: gymId || undefined,
        from: from || undefined,
        to: to || undefined,
      }),
    [from, to, gymId],
  );
  const rows = recent ?? [];
  const count = (c: string) => stats?.byChannel.find((s) => s.channel === c)?.sent ?? 0;
  const cost = stats?.smsCost;
  const money = (n: number, ccy: string) =>
    `${ccy} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const activePreset = useMemo(
    () => PRESETS.find((p) => p.from === from && p.to === to)?.label ?? 'Custom',
    [from, to],
  );

  return (
    <div>
      <p className="mb-4 text-sm text-ink-soft">
        Every email and SMS we've sent across all gyms. SMS volume drives our messaging cost.
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="SMS sent (30d)" value={count('SMS')} accent />
        <StatCard label="Emails sent (30d)" value={count('EMAIL')} />
        <StatCard
          label={`Est. SMS cost, 30d (${cost?.currency ?? 'USD'})`}
          text={cost ? money(cost.estimatedCost, cost.currency) : '—'}
        />
        <StatCard label="Total sends (30d)" value={stats?.total ?? 0} />
      </div>

      {cost && (
        <p className="mt-2 text-xs text-muted">
          Approx: {cost.econetTelecelCount.toLocaleString()} Econet/Telecel @ {cost.currency}{' '}
          {cost.econetTelecelRate} · {cost.netoneCount.toLocaleString()} NetOne @ {cost.currency} {cost.netoneRate}
          {cost.unknownCount > 0 && ` · ${cost.unknownCount.toLocaleString()} other (est. at NetOne rate)`}.
        </p>
      )}

      <SmsRatesEditor />

      {/* Gym filter */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <label htmlFor="gym-filter" className="text-sm text-ink-soft">
          Gym
        </label>
        <select
          id="gym-filter"
          value={gymId}
          onChange={(e) => setGymId(e.target.value)}
          className="rounded-control border border-line bg-card px-3 py-1.5 text-sm text-ink"
        >
          <option value="">All gyms</option>
          {(gyms ?? [])
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
        </select>
        {gymId && (
          <button
            type="button"
            onClick={() => setGymId('')}
            className="rounded-control px-2 py-1.5 text-sm text-muted hover:text-ink"
          >
            Clear
          </button>
        )}
      </div>

      {/* Date filter */}
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                setFrom(p.from);
                setTo(p.to);
              }}
              className={cn(
                'rounded-pill px-3 py-1.5 text-sm font-medium transition-colors',
                activePreset === p.label ? 'bg-ink text-on-dark' : 'bg-card text-ink-soft hover:bg-surface',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs text-ink-soft">
            From
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 block rounded-control border border-line px-2.5 py-1.5 text-sm text-ink"
            />
          </label>
          <label className="text-xs text-ink-soft">
            To
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 block rounded-control border border-line px-2.5 py-1.5 text-sm text-ink"
            />
          </label>
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="rounded-control px-2 py-1.5 text-sm text-muted hover:text-ink"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <Card className="mt-3 overflow-hidden p-0">
        {loading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-11" />
            ))}
          </div>
        ) : rows.length ? (
          <>
            <p className="border-b border-line px-4 py-2 text-xs text-muted">
              {rows.length.toLocaleString()} message{rows.length === 1 ? '' : 's'}
              {activePreset !== 'All time' && ` · ${activePreset.toLowerCase()}`}
            </p>
            <ul className="divide-y divide-line">
              {rows.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span
                    className={cn(
                      'w-14 shrink-0 rounded-pill px-2 py-0.5 text-center text-xs font-medium',
                      r.channel === 'SMS' ? 'bg-accent-soft text-accent-ink' : 'bg-surface text-ink-soft',
                    )}
                  >
                    {r.channel}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">
                      {r.recipientName || r.destination || 'Unknown recipient'}
                    </p>
                    <p className="truncate text-xs text-muted">
                      {r.event.replace(/_/g, ' ').toLowerCase()}
                      {r.gymName && ` · ${r.gymName}`}
                      {r.recipientName && r.destination && ` · ${r.destination}`}
                    </p>
                  </div>
                  {r.status !== 'SENT' && (
                    <span className={cn('shrink-0 text-xs font-medium', r.status === 'FAILED' ? 'text-bad-ink' : 'text-muted')}>
                      {r.status.toLowerCase()}
                    </span>
                  )}
                  <span className="shrink-0 text-xs text-muted tabular">
                    {new Date(r.createdAt).toLocaleString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="p-6 text-center text-sm text-muted">
            No messages {activePreset === 'All time' ? 'sent yet' : `in this range`}.
          </p>
        )}
      </Card>
    </div>
  );
}

function StatCard({ label, value, text, accent }: { label: string; value?: number; text?: string; accent?: boolean }) {
  return (
    <Card className={cn('p-4', accent && 'bg-accent-soft')}>
      <p className="font-display text-2xl font-semibold text-ink tabular">{text ?? (value ?? 0).toLocaleString()}</p>
      <p className="mt-0.5 text-xs text-muted">{label}</p>
    </Card>
  );
}

/** Editable per-network SMS rates (Econet & Telecel price the same, NetOne differs). */
function SmsRatesEditor() {
  const { data } = useAsync(getPlatformSettings);
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [econet, setEconet] = useState('');
  const [netone, setNetone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) {
      setSettings(data);
      setEconet(String(data.smsRateEconetTelecel));
      setNetone(String(data.smsRateNetone));
    }
  }, [data]);

  async function save() {
    if (!settings || saving) return;
    setSaving(true);
    setSaved(false);
    try {
      const res = await updatePlatformSettings({
        annualDiscountPercent: settings.annualDiscountPercent,
        trialDays: settings.trialDays,
        subscriptionReminderLeadDays: settings.subscriptionReminderLeadDays,
        subscriptionGraceDays: settings.subscriptionGraceDays,
        smsRateEconetTelecel: parseFloat(econet) || 0,
        smsRateNetone: parseFloat(netone) || 0,
      });
      setSettings(res);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally {
      setSaving(false);
    }
  }

  const ccy = settings?.smsRateCurrency ?? 'USD';

  return (
    <Card className="mt-4 flex flex-col gap-3 p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-ink">SMS rates ({ccy}/message)</h3>
          <p className="mt-0.5 text-sm text-muted">Used to estimate cost above. Update when your provider's pricing changes.</p>
        </div>
        {saved && <span className="text-sm font-medium text-ok-ink">Saved</span>}
      </div>
      <div className="grid max-w-md grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="text-ink-soft">Econet &amp; Telecel</span>
          <input
            inputMode="decimal"
            value={econet}
            onChange={(e) => setEconet(e.target.value.replace(/[^0-9.]/g, ''))}
            className="mt-1 w-full rounded-control border border-line px-3 py-2 text-ink"
          />
        </label>
        <label className="text-sm">
          <span className="text-ink-soft">NetOne</span>
          <input
            inputMode="decimal"
            value={netone}
            onChange={(e) => setNetone(e.target.value.replace(/[^0-9.]/g, ''))}
            className="mt-1 w-full rounded-control border border-line px-3 py-2 text-ink"
          />
        </label>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-pill bg-ink px-4 py-2 text-sm font-medium text-on-dark disabled:opacity-50"
        >
          Save rates
        </button>
      </div>
    </Card>
  );
}
